/* eslint-disable camelcase */
import type { GetStaticPaths, GetStaticProps } from 'next'
import { isNil } from 'ramda'

import type {
  ArticleListResponse,
  CategoryListResponseDataItemCustom,
  NavigationResponse,
  StaticTextsConfig,
} from '@twisto/api-cms'

import { Navigation } from 'homepage/components/navigation'
import { AdditionalDataProvider } from 'homepage/libs/context'
import {
  getCategories,
  getCategory,
  getNavigation,
  getPopularArticles,
  getStaticTexts,
} from 'homepage/utils/api'
import { transformPaths } from 'homepage/utils/paths'
import { CategoryOverview, MainCategoryOverview } from 'homepage/views/'

type StaticProps = {
  navigation?: NavigationResponse
  category?: CategoryListResponseDataItemCustom['attributes']
  pageContext: { path?: string | string[] }
  popularArticles?: ArticleListResponse
  staticTexts: StaticTextsConfig
}

const DynamicPage = ({
  navigation,
  category,
  popularArticles,
  staticTexts,
}: StaticProps) => {
  const isMainCategory = isNil(category?.parent)
  const navigationData = navigation?.data

  if (isNil(category)) {
    return {
      notFound: true,
    }
  }

  return (
    <AdditionalDataProvider
      additionalData={{
        eshopsData: { eshops: [], eshop_categories: [] },
        cashbackData: { benefits: [], affiliate_benefits: [] },
        mediaArticlesData: null,
        staticTexts,
      }}
    >
      {!isNil(navigationData) && <Navigation {...navigationData} />}
      {category.categories && (
        <>
          {isMainCategory ? (
            <MainCategoryOverview
              category={category}
              popularArticles={popularArticles}
            />
          ) : (
            <CategoryOverview {...category} />
          )}
        </>
      )}
    </AdditionalDataProvider>
  )
}

export const getStaticPaths = (async () =>
  transformPaths(await getCategories())) satisfies GetStaticPaths

export const getStaticProps = (async (context) => {
  const { params } = context
  const url = [params?.path ?? ''].flat().join('/')
  const pageData = await getCategory(`/category/${url}`)

  if (isNil(pageData.data)) {
    return {
      notFound: true,
    }
  }

  const attributes = pageData.data
  const navigationId = attributes.navigation?.id ?? null
  const popularArticles = await getPopularArticles(pageData.data.id)
  const staticTexts = await getStaticTexts()

  const navigation = !isNil(navigationId)
    ? await getNavigation(navigationId)
    : undefined

  return {
    props: {
      category: attributes,
      navigation: navigation ?? null,
      popularArticles,
      pageContext: {
        path: attributes.path ?? null,
      },
      staticTexts: staticTexts.data,
    },

    revalidate: 1000,
  }
}) satisfies GetStaticProps

export default DynamicPage
